import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Play, Plus, CheckCircle2, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useSkillGoldenCases,
  useSkillGoldenRuns,
  useRunGolden,
  useAddGoldenCase,
  type SkillRegistryRow,
} from "@/hooks/useSkillDetail";

interface Props {
  skill: SkillRegistryRow;
}

export function GoldenTab({ skill }: Props) {
  const { toast } = useToast();
  const cases = useSkillGoldenCases(skill.id);
  const runs = useSkillGoldenRuns(skill.id, 30);
  const runGolden = useRunGolden();
  const addCase = useAddGoldenCase();

  const lastRunByCase = new Map<string, ReturnType<typeof runs.data extends infer T ? (T extends Array<infer U> ? () => U : never) : never>>();
  // Simpler: build last run map directly
  const lastByCase: Record<string, { passed: boolean; failure_reason: string | null; duration_ms: number | null }> = {};
  for (const r of runs.data ?? []) {
    if (!lastByCase[r.case_id]) {
      lastByCase[r.case_id] = {
        passed: r.passed,
        failure_reason: r.failure_reason,
        duration_ms: r.duration_ms,
      };
    }
  }

  const total = cases.data?.length ?? 0;
  const passedCount = cases.data?.filter((c) => lastByCase[c.id]?.passed).length ?? 0;
  const adversarialCount = cases.data?.filter((c) => (c.context_metadata as Record<string, unknown> | null)?.adversarial === true).length ?? 0;
  const passRate = total > 0 ? passedCount / total : 0;

  const onRun = () => {
    runGolden.mutate(
      { skill_id: skill.id },
      {
        onSuccess: (data) =>
          toast({
            title: "Golden run completo",
            description: `${data.passed}/${data.total} passaram (${Math.round(data.pass_rate * 100)}%).`,
          }),
        onError: (e) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
      },
    );
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-base">Golden cases</CardTitle>
              <CardDescription>Validação obrigatória antes de promover para canary/on.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{total} casos</Badge>
              <Badge variant={adversarialCount >= 2 ? "default" : "outline"}>{adversarialCount} adversariais</Badge>
              <Badge variant={passRate >= 0.8 ? "default" : "destructive"}>
                {total > 0 ? `${Math.round(passRate * 100)}% pass` : "sem run"}
              </Badge>
              <Button onClick={onRun} disabled={runGolden.isPending || total === 0} size="sm">
                {runGolden.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                Rodar todos
              </Button>
              <AddCaseDialog skill={skill} onAdded={() => cases.refetch()} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {cases.isLoading ? (
            <div className="text-xs text-muted-foreground">Carregando…</div>
          ) : total === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              Nenhum golden case ainda. Adicione ao menos 8 (incluindo 2 adversariais) para liberar publish.
            </div>
          ) : (
            <div className="space-y-2">
              {cases.data!.map((c) => {
                const last = lastByCase[c.id];
                const adversarial = (c.context_metadata as Record<string, unknown> | null)?.adversarial === true;
                return (
                  <div key={c.id} className="flex items-start gap-3 p-3 border rounded-md text-sm">
                    <div className="mt-0.5">
                      {!last ? (
                        <Badge variant="outline" className="text-xs">não rodado</Badge>
                      ) : last.passed ? (
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                      ) : (
                        <XCircle className="w-4 h-4 text-destructive" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{c.name}</span>
                        {adversarial && <Badge variant="outline" className="text-[10px]">adversarial</Badge>}
                        {c.expected_metacog_decision && (
                          <Badge variant="outline" className="text-[10px]">→ {c.expected_metacog_decision}</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-2">{c.input_message}</div>
                      {last && !last.passed && last.failure_reason && (
                        <div className="text-xs text-destructive mt-1">{last.failure_reason}</div>
                      )}
                    </div>
                    {last?.duration_ms != null && (
                      <Badge variant="outline" className="text-[10px]">{last.duration_ms}ms</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AddCaseDialog({ skill, onAdded }: { skill: SkillRegistryRow; onAdded: () => void }) {
  const { toast } = useToast();
  const add = useAddGoldenCase();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [input, setInput] = useState("");
  const [expected, setExpected] = useState("");
  const [adversarial, setAdversarial] = useState(false);

  const onSubmit = () => {
    if (!skill.org_id) {
      toast({ title: "Skill global não suporta golden cases", variant: "destructive" });
      return;
    }
    const expectedArr = expected
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    add.mutate(
      {
        org_id: skill.org_id,
        skill_id: skill.id,
        name: name.trim() || `case-${Date.now()}`,
        input_message: input.trim(),
        expected_output_contains: expectedArr.length > 0 ? expectedArr : null,
        context_metadata: adversarial ? { adversarial: true } : {},
      },
      {
        onSuccess: () => {
          toast({ title: "Caso adicionado" });
          setOpen(false);
          setName("");
          setInput("");
          setExpected("");
          setAdversarial(false);
          onAdded();
        },
        onError: (e) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Plus className="w-4 h-4 mr-1" /> Adicionar caso
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo golden case</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Nome</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex: saudação básica" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Mensagem de entrada</label>
            <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={3} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Saída esperada contém (separado por vírgula)</label>
            <Input value={expected} onChange={(e) => setExpected(e.target.value)} placeholder="palavra1, frase2" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={adversarial} onChange={(e) => setAdversarial(e.target.checked)} />
            Marcar como adversarial
          </label>
        </div>
        <DialogFooter>
          <Button onClick={onSubmit} disabled={add.isPending || !input.trim()}>
            {add.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
